import restClient from "api/restClient";
import NotificationService from "components/Notification/Notification.service";
import { toast, TypeOptions } from "react-toastify";

/**
 * Enabling git on a project/bundle/workspace with many tracked records returns
 * as soon as the repository exists; the backend exports the records in the
 * background and reports progress at `GET <type>/<id>/git/enable-status`.
 * This watcher polls that endpoint and keeps one toast up to date
 * (N/total), then turns it into a success, warning or error message.
 */

export type EnableGitStatus = {
    state: "NONE" | "PENDING" | "RUNNING" | "SUCCEEDED" | "FAILED";
    total?: number;
    done?: number;
    skipped_count?: number;
    skipped?: { record_id?: string; name?: string; error?: string }[];
    error?: string | null;
};

export const ENABLE_GIT_POLL_MS = 5000;

export const isRunningEnableGitStatus = (status?: EnableGitStatus | null) =>
    status?.state === "PENDING" || status?.state === "RUNNING";

export const describeEnableGitStatus = (
    status: EnableGitStatus,
    label: string
): { text: string; type: TypeOptions } => {
    const total = status.total ?? 0;
    const done = status.done ?? 0;
    const skipped = status.skipped_count ?? 0;
    switch (status.state) {
        case "PENDING":
        case "RUNNING":
            return {
                text: `Exporting ${label} to git: ${done} of ${total} records`,
                type: "info",
            };
        case "SUCCEEDED":
            if (skipped > 0) {
                const names = (status.skipped ?? [])
                    .map((s) => s.name)
                    .filter(Boolean)
                    .slice(0, 5)
                    .join(", ");
                return {
                    text: `Exported ${label} to git: ${
                        total - skipped
                    } of ${total} records. ${skipped} could not be exported${
                        names ? ` (${names})` : ""
                    }.`,
                    type: "warning",
                };
            }
            return { text: `Exported ${label} to git: ${total} records`, type: "success" };
        case "FAILED":
            return {
                text: `Exporting ${label} to git failed after ${done} of ${total} records: ${
                    status.error ?? "unknown error"
                }`,
                type: "error",
            };
        default:
            return { text: "", type: "default" };
    }
};

const fetchStatus = (resourceType: string, containerId: string) =>
    restClient
        .get(`${resourceType}/${containerId}/git/enable-status`)
        .then((res: any) => (res?.data ?? null) as EnableGitStatus | null);

/**
 * Check once whether a background export started; if so, keep a progress toast
 * updated until it ends. Resolves with the final status (or null when there was
 * no background export, e.g. a small container exported inline).
 */
export const watchEnableGitExport = async ({
    resourceType,
    containerId,
    label,
    pollMs = ENABLE_GIT_POLL_MS,
    onFinished,
}: {
    resourceType: string;
    containerId: string;
    label: string;
    pollMs?: number;
    onFinished?: (status: EnableGitStatus) => void;
}): Promise<EnableGitStatus | null> => {
    let status: EnableGitStatus | null;
    try {
        status = await fetchStatus(resourceType, containerId);
    } catch {
        return null;
    }
    if (!isRunningEnableGitStatus(status)) {
        return null;
    }
    const first = describeEnableGitStatus(status as EnableGitStatus, label);
    const toastId = NotificationService.info(first.text, { autoClose: false });
    let failures = 0;
    while (isRunningEnableGitStatus(status)) {
        await new Promise((resolve) => setTimeout(resolve, pollMs));
        try {
            status = await fetchStatus(resourceType, containerId);
            failures = 0;
        } catch {
            failures += 1;
            if (failures >= 5) {
                toast.update(toastId, {
                    render: `Lost track of the git export of ${label}. Open "Manage git" later to check the repository.`,
                    type: "warning",
                    autoClose: 15000,
                });
                return null;
            }
            continue;
        }
        if (!status || status.state === "NONE") {
            toast.dismiss(toastId);
            return null;
        }
        const next = describeEnableGitStatus(status, label);
        const running = isRunningEnableGitStatus(status);
        toast.update(toastId, {
            render: next.text,
            type: next.type,
            autoClose: running ? false : next.type === "success" ? 5000 : 15000,
        });
    }
    if (status) {
        onFinished?.(status);
    }
    return status;
};
